<template>
	<v-form ref="form" v-model="valid">
		<v-stepper v-model="pageNumber" elevation="0">
			<v-stepper-header class="elevation-0">
				<v-stepper-step :complete="pageNumber > 1" step="1"> 初期設定 </v-stepper-step>
				<v-divider></v-divider>
				<v-stepper-step :complete="pageNumber > 2" step="2"> 基本設定 </v-stepper-step>
				<v-divider></v-divider>
				<v-stepper-step step="3"> 検索フォーム設定 </v-stepper-step>
			</v-stepper-header>
			<v-stepper-items>
				<v-stepper-content step="1" class="pa-0">
					<v-card class="ma-2 pa-0" elevation="4">
						<v-app-bar dense dark flat color="primary">
							<v-app-bar-title class="color-white"> 台帳初期設定 </v-app-bar-title>
							<v-spacer />
						</v-app-bar>

						<div class="pa-2">
							<v-text-field
								v-model="ledger.name"
								label="台帳名(必須)"
								class="mt-4"
								type="text"
								:rules="[rules.required]"
								required
								dense
								outlined
								@change="changeLedger"
							/>
							<v-select
								v-model="ledger.base_model"
								label="参照データ(必須)"
								:items="modelNames"
								class="mt-4"
								item-text="name"
								item-value="id"
								:rules="[rules.required]"
								required
								dense
								outlined
								:disabled="isDetermined"
								:hint="
									isDetermined
										? '参照データは変更できません。'
										: '台帳の行のベースとなるデータです。'
								"
								:persistent-hint="isDetermined"
								@change="changeLedger"
							/>
							<v-select
								v-model="ledger.department_id"
								label="部門"
								:items="departments"
								class="mt-4"
								item-text="name"
								item-value="id"
								required
								dense
								clearable
								outlined
								@change="changeLedger"
							/>
							<v-checkbox
								class="shrink"
								v-model="ledger.exclusive_flag"
								label="自身専用の台帳"
								@change="changeLedger"
								:true-value="true"
								:false-value="false"
							/>
							<v-row no-gutters class="my-4" justify="center">
								<v-btn
									color="secondary"
									:disabled="!isLedgerFormFilled"
									@click="isDetermined ? renderPage(2) : determineBaseModel()"
								>
									<v-icon class="mr-2">mdi-page-next</v-icon>
									決定して次へ進む</v-btn
								>
							</v-row>
						</div>
					</v-card>
				</v-stepper-content>

				<v-stepper-content step="2" class="pa-0">
					<v-card class="ma-2 pa-0" elevation="4">
						<v-app-bar dense dark flat color="primary">
							<v-app-bar-title class="color-white"> 台帳基本設定 </v-app-bar-title>
							<v-spacer />
							<v-btn @click="renderPage(1)" text color="white">
								<v-icon class="mr-2">mdi-arrow-left-circle</v-icon>
								初期設定へ戻る</v-btn
							>
						</v-app-bar>
						<v-form ref="basicSettingForm" lazy-validation>
							<div class="pa-2">
								<v-row no-gutters justify="space-between">
									<h6 class="text-h6 font-weight-bold">列の設定</h6>
									<v-row no-gutters justify="end">
										<v-btn color="secondary" @click="addColumn">
											<v-icon class="mr-2">mdi-table-column-plus-before</v-icon>
											列を追加</v-btn
										>
									</v-row>
								</v-row>
								<draggable
									v-if="ledger.ledger_columns_attributes.length"
									tag="div"
									handle=".handle"
									@end="sortLedgerColumns"
									:options="{ animation: 300 }"
								>
									<LedgerColumnForm
										v-for="(ledgerColumn, i) in filteredArray(ledger.ledger_columns_attributes)"
										:ledgerColumnData="ledgerColumn"
										:columnOptions="columnOptions"
										:originalModelColumns="originalModelColumns"
										:associationNames="associationNames"
										:key="ledgerColumn.uuid"
										@delete-ledger-column="deleteLedgerColumn(i)"
										@change-ledger-column="changeLedgerColumn($event, i)"
									/>
								</draggable>
								<v-card v-else class="pa-4" elevation="0">
									<v-card-text class="bg-grey">
										台帳に表示される列を設定できます。
										<br />
										追加した列は左から順番に表示されます。
										<br />
										この画面に列の順番はこのアイコン<v-icon>mdi-drag</v-icon>
										をドラッグすることで入れ替えが可能です。
										<br />
										表示する項目は、初期設定で指定した参照データの項目、またはそれに紐付くデータの項目から選択できます。
										<br />
										台帳で直接データの編集できるかを設定できます。
										<br />
										選択した項目が数値なら計算式を設定することも可能です。
									</v-card-text>
								</v-card>
								<v-row no-gutters justify="space-between">
									<h6 class="text-h6 font-weight-bold">初期フィルターの設定</h6>
									<v-row no-gutters justify="end">
										<v-btn color="secondary" @click="addFilter">
											<v-icon class="mr-2">mdi-filter-plus</v-icon>
											初期フィルターを追加</v-btn
										>
									</v-row>
								</v-row>
								<div v-if="ledger.ledger_filters_attributes.length">
									<LedgerFilterForm
										v-for="(ledgerFilter, i) in filteredArray(ledger.ledger_filters_attributes)"
										:ledgerFilterData="ledgerFilter"
										:filterOptions="filterOptions"
										:originalModelColumns="originalModelColumns"
										:associationNames="associationNames"
										:key="ledgerFilter.uuid"
										:count="i"
										@delete-ledger-filter="deleteLedgerFilter(i)"
										@change-ledger-filter="changeLedgerFilter($event, i)"
									/>
								</div>
								<v-card v-else class="pa-4" elevation="0">
									<v-card-text class="bg-grey">
										台帳に表示されるデータの初期フィルターを設定できます。
										<br />
										設定した初期フィルターで絞り込まれたデータのみが台帳に表示されます。
										<br />
										次のページで検索フォームも設定できます。
									</v-card-text>
								</v-card>

								<v-row no-gutters class="my-4" justify="center">
									<v-btn color="secondary" @click="renderSearchForm">
										<v-icon class="mr-2">mdi-page-next</v-icon>
										決定して次へ進む</v-btn
									>
								</v-row>
							</div>
						</v-form>
					</v-card>
				</v-stepper-content>

				<v-stepper-content step="3" class="pa-0">
					<v-card class="ma-2 pa-0" elevation="4">
						<v-app-bar dense dark flat color="primary">
							<v-app-bar-title class="color-white"> 検索フォーム設定 </v-app-bar-title>
							<v-spacer />
							<v-btn @click="renderPage(2)" text color="white">
								<v-icon class="mr-2">mdi-arrow-left-circle</v-icon>
								基本設定へ戻る</v-btn
							>
						</v-app-bar>
						<div class="pa-2">
							<v-row no-gutters justify="space-between">
								<h6 class="text-h6 font-weight-bold">検索フォームの設定</h6>
								<v-row no-gutters justify="end">
									<v-btn color="secondary" @click="addSearchForm">
										<v-icon class="mr-2">mdi-filter-variant-plus</v-icon>
										検索フォームを追加</v-btn
									>
								</v-row>
							</v-row>
							<div v-if="ledger.ledger_search_forms_attributes.length">
								<LedgerSearchFormForm
									v-for="(ledgerSearchForm, i) in filteredArray(
										ledger.ledger_search_forms_attributes
									)"
									:ledgerSearchFormData="ledgerSearchForm"
									:searchOptions="searchOptions"
									:originalModelColumns="originalModelColumns"
									:associationNames="associationNames"
									:key="ledgerSearchForm.uuid"
									:count="i"
									@delete-ledger-search-form="deleteLedgerSearchForm(i)"
									@change-ledger-search-form="changeLedgerSearchForm($event, i)"
								/>
							</div>
							<v-card v-else class="pa-4" elevation="0">
								<v-card-text class="bg-grey">
									絞り込む検索用の検索フォームを設定できます。
									<br />
									指定する項目は、初期設定で指定した参照データの項目、またはそれに紐付くデータの項目から選択できます。
									<br />
									項目名に合わせて条件の選択肢が選択できるようになります。
									<br />
									条件に合わせて入力フォームの選択肢が選択できるようになります。
									<br />
									選択した入力フォームによっては、入力フォームに表示される選択肢を追加することができます。
								</v-card-text>
							</v-card>
							<v-row no-gutters justify="space-between">
								<h6 class="text-h6 font-weight-bold">並び替え項目の設定</h6>
								<v-row no-gutters justify="end">
									<v-btn color="secondary" @click="addSearchSort">
										<v-icon class="mr-2">mdi-sort</v-icon>
										並び替え項目を追加</v-btn
									>
								</v-row>
							</v-row>
							<div v-if="ledger.ledger_sorts_attributes.length">
								<LedgerSortForm
									v-for="(ledgerSort, i) in filteredArray(ledger.ledger_sorts_attributes)"
									:ledgerSortData="ledgerSort"
									:originalModelColumns="originalModelColumns"
									:associationNames="associationNames"
									:key="ledgerSort.uuid"
									:count="i"
									@delete-ledger-sort="deleteLedgerSort(i)"
									@change-ledger-sort="changeLedgerSort($event, i)"
								/>
							</div>
							<v-card v-else class="pa-4" elevation="0">
								<v-card-text class="bg-grey">
									検索フォームの並び替えの選択肢を設定できます。
									<br />
									初期設定で指定した参照データの項目から項目名を選択できます。
									<br />
									表示名は自身で設定することができます。
								</v-card-text>
							</v-card>
							<v-row no-gutters class="my-4" justify="center">
								<v-btn color="secondary" @click="submitForm" :disabled="!valid">
									<v-icon class="mr-2">mdi-plus</v-icon>
									登録する</v-btn
								>
							</v-row>
						</div>
					</v-card>
				</v-stepper-content>
			</v-stepper-items>
		</v-stepper>
	</v-form>
</template>

<script>
import axios from "axios"
import draggable from 'vuedraggable'
import rules from "../../../helper/rules"
import authenticate from "../../../helper/authenticate"
import errorHandling from "../../../helper/errorHandling"
import LedgerColumnForm from "./LedgerColumnForm.vue"
import LedgerFilterForm from "./LedgerFilterForm.vue"
import LedgerSearchFormForm from "./LedgerSearchFormForm.vue"
import LedgerSortForm from "./LedgerSortForm.vue"
export default {
	props: {
		ledgerData: Object,
		modelNames: Array,
		departments: Array,
		columnOptions: Object,
		filterOptions: Object,
		searchOptions: Object,
		associationNameData: Array,
		columnData: Array,
	},
	components: {
		draggable,
		LedgerColumnForm,
		LedgerFilterForm,
		LedgerSearchFormForm,
		LedgerSortForm,
	},
	data() {
		return {
			rules: rules,
			ledger: {
				name: null,
				base_model: null,
				exclusive_flag: null,
				department_id: null,
				ledger_columns_attributes: [],
				ledger_filters_attributes: [],
				ledger_search_forms_attributes: [],
				ledger_sorts_attributes: [],
			},
			isDetermined: false,
			valid: false,
			originalModelColumns: [],
			associationNames: [],
			pageNumber: 1,
		}
	},
	methods: {
		submitForm() {
			if (!this.$refs.form.validate()) return
			this.$emit("submit")
		},
		determineBaseModel() {
			const headers = authenticate.setHeader()
			const url = "/erp/v1/ledgers/models"
			axios
				.get(url, { params: { base_model: this.ledger.base_model }, headers })
				.then((res) => {
					this.originalModelColumns = res.data.columns
					this.associationNames = res.data.association_names
					this.isDetermined = true
					this.renderPage(2)
				})
				.catch((err) => errorHandling.errorResponse(err))
		},
		changeLedger() {
			this.$emit("change-ledger", this.ledger)
		},
		renderSearchForm() {
			if (!this.$refs.basicSettingForm.validate()) return
			this.renderPage(3)
		},
		renderPage(num) {
			this.pageNumber = num
			window.scrollTo({
				top: 0,
				behavior: "smooth",
			})
		},
		deleteChildForm(array, i) {
			const child = this.filteredArray(array)[i]
			const index = array.indexOf(child)
			if (child.id) {
				array.push({
					id: child.id,
					_destroy: true,
				})
			}
			array.splice(index, 1)
		},
		addColumn() {
			const newColumn = {
				name: null,
				uuid: this.newUuid(),
				order_number: this.filteredArray(this.ledger.ledger_columns_attributes).length,
				base_model: null,
				column_name: null,
				column_type: null,
				meta_tag_id: null,
				editable_flag: null,
				prefix_formula: null,
				prefix_number: null,
				suffix_formula: null,
				suffix_number: null,
				special_type: null,
				special_string: null,
			}
			this.ledger.ledger_columns_attributes.push(newColumn)
			this.changeLedger()
		},
		deleteLedgerColumn(i) {
			this.deleteChildForm(this.ledger.ledger_columns_attributes, i)

			this.filteredArray(this.ledger.ledger_columns_attributes).forEach(
				(_, orderNumber, columns) => (columns[orderNumber].order_number = orderNumber)
			)
			this.changeLedger()
		},
		changeLedgerColumn(ledgerColumn, i) {
			this.filteredArray(this.ledger.ledger_columns_attributes)[i] = ledgerColumn
			this.changeLedger()
		},
		sortLedgerColumns(e) {
			const oldIndex = e.oldIndex
			const newIndex = e.newIndex
			this.filteredArray(this.ledger.ledger_columns_attributes).forEach((c, _, columns) => {
				if (c.order_number == oldIndex) {
					columns[c.order_number].order_number = newIndex
				} else if (c.order_number > oldIndex && c.order_number <= newIndex) {
					columns[c.order_number].order_number -= 1
				} else if (c.order_number < oldIndex && c.order_number >= newIndex) {
					columns[c.order_number].order_number += 1
				}
			})
			this.ledger.ledger_columns_attributes.sort((a, b) => {
				if (a._destroy) return 1
				if (a.order_number < b.order_number) return -1
				if (a.order_number > b.order_number) return 1
				return 0
			})
			this.changeLedger()
		},
		addFilter() {
			const newFilter = {
				uuid: this.newUuid(),
				base_model: null,
				column_name: null,
				value: null,
				condition: null,
			}
			this.ledger.ledger_filters_attributes.push(newFilter)
			this.changeLedger()
		},
		deleteLedgerFilter(i) {
			this.deleteChildForm(this.ledger.ledger_filters_attributes, i)
			this.changeLedger()
		},
		changeLedgerFilter(ledgerFilter, i) {
			this.filteredArray(this.ledger.ledger_filters_attributes)[i] = ledgerFilter
			this.changeLedger()
		},
		addSearchForm() {
			const newSearchForm = {
				uuid: this.newUuid(),
				base_model: null,
				column_name: null,
				condition: null,
				form_type: null,
				ledger_search_options_attributes: [],
			}
			this.ledger.ledger_search_forms_attributes.push(newSearchForm)
			this.changeLedger()
		},
		deleteLedgerSearchForm(i) {
			this.deleteChildForm(this.ledger.ledger_search_forms_attributes, i)
			this.changeLedger()
		},
		changeLedgerSearchForm(ledgerSearchForm, i) {
			this.filteredArray(this.ledger.ledger_search_forms_attributes)[i] = ledgerSearchForm
			this.changeLedger()
		},
		addSearchSort() {
			const newSort = {
				uuid: this.newUuid(),
				// base_model: null,
				column_name: null,
				sort_type: null,
				name: null,
				value: null,
			}
			this.ledger.ledger_sorts_attributes.push(newSort)
			this.changeLedger()
		},
		deleteLedgerSort(i) {
			this.deleteChildForm(this.ledger.ledger_sorts_attributes, i)
			this.changeLedger()
		},
		changeLedgerSort(ledgerSort, i) {
			this.filteredArray(this.ledger.ledger_sorts_attributes)[i] = ledgerSort
			this.changeLedger()
		},
		newUuid() {
			return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
				(c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
			)
		},
	},
	computed: {
		isLedgerFormFilled() {
			return this.ledger.name && this.ledger.base_model
		},
		filteredArray() {
			return (array) => {
				return array.filter((attribute) => !attribute._destroy)
			}
		},
	},
	watch: {
		ledgerData: {
			handler(newValue, _) {
				this.ledger = newValue
			},
		},
		associationNameData: {
			handler(newValue, _) {
				this.associationNames = newValue
			},
		},
		columnData: {
			handler(newValue, _) {
				this.originalModelColumns = newValue
			},
		},
		immediate: true,
		deep: true,
	},
}
</script>å